import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import GrainTexture from '@/components/GrainTexture';
import systemiqueLogo from '@/assets/systemique.png';
import systemiqueLogoWebp from '@/assets/systemique.webp';
import termsPdf from '@/assets/Algemene-voorwaarden-Systemique.pdf';
import { EmailLink } from '@/components/EmailLink';
import {
  SITE_LAST_UPDATED,
  formatSiteLastUpdatedNl,
} from '@/constants/siteLastUpdated';

const Footer: React.FC = () => {

  return (
    <footer className="bg-[#854C73] text-white pt-20 pb-10 relative overflow-hidden">
      <GrainTexture opacity={0.15} />
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 md:col-span-2">
            <OptimizedImage 
              src={systemiqueLogo} 
              webpSrc={systemiqueLogoWebp} 
              alt="Systemique - Systemisch werk en coaching" 
              className="h-10 w-auto mb-6 brightness-0 invert" 
              width={120}
              height={40}
            />
            <p className="text-white/80 text-lg leading-relaxed max-w-md mb-8 font-light">
              "Beweging brengen in dat wat vast zit." Of je dat nu zelf bent, of dat je niet meer weet hoe jij je organisatie of team in beweging krijgt.
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-6 uppercase tracking-widest text-sm opacity-60">Diensten</h4>
            <ul className="space-y-3">
              {[
                { label: 'Aanbod overzicht', path: '/aanbod', title: 'Bekijk alle diensten van Systemique' },
                { label: 'Familieopstellingen', path: '/familieopstellingen', title: 'Familieopstellingen in Castricum, Amsterdam en Uitgeest' },
                { label: 'Organisatieopstellingen', path: '/organisatieopstellingen', title: 'Organisatieopstellingen Castricum, Amsterdam en Noord-Holland' },
                { label: 'Teamcoaching', path: '/teamcoaching', title: 'Systemische teamcoaching Amsterdam, Haarlem, Noord-Holland of Utrecht' },
                { label: 'Leergang systemisch leiderschap', path: '/leergang-systemisch-leiderschap', title: 'Leergang systemisch leiderschap voor leiders en adviseurs in Amsterdam & Noord-Holland' },
                { label: 'Opleidingstrajecten', path: '/opleidingstrajecten', title: 'Systemische trainingen en opleidingen' },
                { label: '1-op-1 coaching', path: '/1-op-1', title: '1-op-1 systemische coaching' }
              ].map(item => (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    title={item.title}
                    className="text-white/80 hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-6 uppercase tracking-widest text-sm opacity-60">Informatie</h4>
            <ul className="space-y-3">
              {[
                { label: 'Over Systemique', path: '/over', title: 'Systemisch coach in Noord-Holland - Over Marthe van der Molen' },
                { label: 'Agenda', path: '/agenda', title: 'Aankomende opstellingenmiddagen en events' },
                { label: 'Contact', path: '/contact', title: 'Contact systemisch coach Castricum en Noord-Holland' },
                { label: 'Strippenkaart kopen', path: '/koop-strippenkaart', title: 'Koop strippenkaart voor opstellingenmiddagen' },
                { label: 'Nieuwsbrief', path: '/nieuwsbrief', title: 'Aanmelden voor de nieuwsbrief van Systemique' }
              ].map(item => (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    title={item.title}
                    className="text-white/80 hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href={termsPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Algemene voorwaarden Systemique"
                  className="text-white/80 hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                >
                  Algemene voorwaarden
                </a>
              </li>
              <li>
                <Link
                  to="/privacy-en-cookies"
                  title="Privacy- en cookiebeleid"
                  className="text-white/80 hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                >
                  Privacy & Cookies
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-6 uppercase tracking-widest text-sm opacity-60">Contact</h4>
            <ul className="space-y-3 text-white/80">
              <li className="flex items-center gap-3">
                <Mail size={18} aria-hidden="true" />
                <EmailLink className="hover:text-white transition-colors" />
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} aria-hidden="true" />
                <a href="tel:+31612991453" className="hover:text-white transition-colors">
                  +31 6 1299 1453
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={18} aria-hidden="true" />
                <span>Eigen praktijk in Castricum<br /><span className="text-sm">Noord Holland, 20 min van Amsterdam. Werkgebied heel Nederland</span></span>
              </li>
              <li className="flex items-center gap-3">
                <Instagram size={18} aria-hidden="true" />
                <a 
                  href="https://www.instagram.com/systemique.nl/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  @systemique.nl
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 text-center text-sm text-white/60 space-y-1">
          <p>
            Laatst bijgewerkt:{' '}
            <time dateTime={SITE_LAST_UPDATED}>{formatSiteLastUpdatedNl()}</time>
          </p>
          <p>© {new Date().getFullYear()} Systemique. Alle rechten voorbehouden.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

